import type { StatsMsg } from "../sdk";

// The live mesh panel — shown inline on the playground while "Run on mesh" is on. It
// surfaces the coordinator's worker-population broadcast (total / idle / busy / active jobs)
// so the distributed-compute story is legible: you can watch workers join and chunks fan out
// as your job runs. Pure DOM, fed by the StatsMsg stream the mesh runner forwards.

export interface MeshPanel {
  el: HTMLElement;
  update(s: StatsMsg): void;
  setConnState(state: "idle" | "connecting" | "running" | "done" | "error", detail?: string): void;
  reset(): void;
}

export function createMeshPanel(): MeshPanel {
  const el = document.createElement("div");
  el.className = "mesh-panel";
  el.innerHTML = `
    <div class="mesh-head">
      <span class="mesh-dot idle" data-dot></span>
      <span class="mesh-title">Mesh</span>
      <span class="mesh-state" data-state>off</span>
    </div>
    <div class="mesh-stats">
      <div class="mesh-stat"><span class="m-val" data-total>—</span><span class="m-lab">workers</span></div>
      <div class="mesh-stat"><span class="m-val" data-busy>—</span><span class="m-lab">computing</span></div>
      <div class="mesh-stat"><span class="m-val" data-idle>—</span><span class="m-lab">idle</span></div>
      <div class="mesh-stat"><span class="m-val" data-jobs>—</span><span class="m-lab">active jobs</span></div>
    </div>
    <div class="mesh-hint muted" data-hint>
      Open <a href="#contribute" target="_blank" rel="noopener">a worker tab</a> (or share the link)
      to add compute — anyone on the web can join.
    </div>
  `;

  const q = <T extends HTMLElement>(sel: string) => el.querySelector<T>(sel)!;

  function update(s: StatsMsg) {
    q("[data-total]").textContent = String(s.total);
    q("[data-busy]").textContent = String(s.busy);
    q("[data-idle]").textContent = String(s.idle);
    q("[data-jobs]").textContent = String(s.activeJobs);
  }

  function setConnState(state: "idle" | "connecting" | "running" | "done" | "error", detail?: string) {
    const dot = q("[data-dot]");
    const label = q("[data-state]");
    const map: Record<string, [string, string]> = {
      idle: ["off", "idle"],
      connecting: ["connecting…", "busy"],
      running: ["running on the mesh", "live"],
      done: ["complete", "live"],
      error: [detail || "error", "err"],
    };
    const [text, cls] = map[state]!;
    label.textContent = text;
    dot.className = "mesh-dot " + cls;
  }

  function reset() {
    for (const sel of ["[data-total]", "[data-busy]", "[data-idle]", "[data-jobs]"]) {
      q(sel).textContent = "—";
    }
    setConnState("idle");
  }

  return { el, update, setConnState, reset };
}
